import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { adminApi } from '../../../config/axios';
import { useGlobal } from '../../../contexts/GlobalContext';
import { usePackages } from '../../../contexts/PackagesContext';
import Spinner from '../../../components/ui/Spinner';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';

const STEPS = ['Plan Setup', 'Features', 'Media & Layers', 'Pricing'];

export default function PackageWizard({ onDone }) {
  const { showToast }       = useGlobal();
  const {
    selectedPackage, selectedFeature, selectedMediaTypes,
    selectedLayers, selectedPeriods, defaultPeriods, gatedFeatures,
    setSelectedFeature, setSelectedMediaTypes, setSelectedLayers, setSelectedPeriods,
    setPeriodPrices,
  } = usePackages();

  const isEdit = Object.keys(selectedPackage).length > 0;
  const [step, setStep]         = useState(0);
  const [saving, setSaving]     = useState(false);
  const [showInDashboard, setShowInDashboard] = useState(false);

  const { control, handleSubmit, trigger, reset, setError, clearErrors, formState: { errors } } = useForm({ mode: 'onChange' });

  // Pre-fill for edit mode — deps intentionally omitted: runs once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEdit) return;
    const features = selectedPackage.features ?? {};
    setShowInDashboard(selectedPackage.show_in_dashboard ?? false);

    const pricing = {};
    const fromRows = (selectedPackage.prices ?? []).map(p => {
      pricing[p.period.toLowerCase()] = p.price;
      return {
        percentage: p.discount_percentage,
        period: p.period.toLowerCase() === 'one_month' ? 1 : p.period.toLowerCase() === 'six_months' ? 6 : 12,
        price: p.price,
        name: p.period.toLowerCase(),
        id: p.id,
      };
    });
    // A package may have no editable (non-gift) pricing rows, or only some of the three
    // standard ones (packages created by the seeders / granted as gifts). `[]` is truthy for
    // `??`, so the old fallback never ran and Step 4 posted `periods: []` (-> 422). Complete the
    // list from the defaults (no id -> the backend creates the missing row). A package that
    // already has all three rows gets exactly the same array as before.
    const periods = [...fromRows, ...defaultPeriods.filter(d => !fromRows.some(r => r.name === d.name))];

    setSelectedPeriods(periods);
    setPeriodPrices(pricing);

    reset({
      title: selectedPackage.title,
      upload_once_content_capacity: parseInt(features.upload_once_content_capacity),
      media_storage_capacity: parseInt(features.media_storage_capacity),
      playlist_capacity: parseInt(features.playlist_capacity),
      email_quota: parseInt(features.email_quota),
      orientation: features.orientation ?? 'landscape',
      price: pricing.one_month ?? 0,
      six_months_price: periods.find(p => p.name === 'six_months')?.percentage ?? 0,
      one_year_price: periods.find(p => p.name === 'one_year')?.percentage ?? 0,
    });

    const activeFeatures = Object.keys(features).filter(k => features[k] === true);
    setSelectedFeature(activeFeatures);
    setSelectedMediaTypes(features.media_type ?? []);
    setSelectedLayers(features.multiple_layer ?? []);
  }, [isEdit]);

  const nextStep = async () => {
    if (step === 0) {
      const valid = await trigger(['title', 'upload_once_content_capacity', 'media_storage_capacity', 'orientation', 'playlist_capacity', 'email_quota']);
      if (!valid) return;
    }
    if (step === 1 && selectedFeature.length === 0) {
      setError('features', { message: 'Select at least one feature.' });
      return;
    }
    if (step === 2) {
      if (selectedLayers.length === 0) { setError('layers', { message: 'One layer is required.' }); return; }
      if (selectedMediaTypes.length === 0) { setError('media_types', { message: 'Select at least one media type.' }); return; }
    }
    setStep(s => s + 1);
  };

  const onSubmit = async (data) => {
    // Guard: only the final step may save. Defends against a spurious submit
    // fired during the Step 3→4 transition (see the button keys/type below).
    if (step < STEPS.length - 1) return;
    setSaving(true);
    const url = isEdit ? `/package/${selectedPackage.id}/update` : '/package/create';
    const body = {
      title: data.title,
      show_in_dashboard: showInDashboard,
      features: {
        // Round-trip: start from the package's ORIGINAL features (edit mode only)
        // so keys this wizard has no control for (youtube_search, karaoke_mode,
        // newer catalogue flags, future keys...) are sent back unchanged instead
        // of being dropped. Everything below overlays the keys the wizard owns,
        // with exactly the values it always produced.
        ...(isEdit && selectedPackage.features && typeof selectedPackage.features === 'object' && !Array.isArray(selectedPackage.features)
          ? selectedPackage.features
          : {}),
        advance_content_schedule: selectedFeature.includes('advance_content_schedule'),
        published_as_grouping: selectedFeature.includes('published_as_grouping'),
        live_meeting_access: selectedFeature.includes('live_meeting_access'),
        full_player_offline: selectedFeature.includes('full_player_offline'),
        interactive_action: selectedFeature.includes('interactive_action'),
        music_list_access: selectedFeature.includes('music_list_access'),
        content_preview: selectedFeature.includes('content_preview'),
        prayer_app: selectedFeature.includes('prayer_app'),
        ai_access: selectedFeature.includes('ai_access'),
        add_users: selectedFeature.includes('add_users'),
        rss_feed: selectedFeature.includes('rss_feed'),
        '3d': selectedFeature.includes('3d'),
        layouts: selectedFeature.includes('layouts'),
        animated_layouts: selectedFeature.includes('animated_layouts'),
        weather: selectedFeature.includes('weather'),
        clock: selectedFeature.includes('clock'),
        device_preview: selectedFeature.includes('device_preview'),
        remote_control: selectedFeature.includes('remote_control'),
        voice_call: selectedFeature.includes('voice_call'),

        // ── Feature Gating: every key enforced by check.feature middleware ──
        // Built from the SAME `gatedFeatures` array Step 2 renders its
        // checkboxes from (fetched from GET /admin/features, which serves
        // config/features.php). This used to be a hand-written mirror of that
        // list, and because this object replaces packages.features wholesale on
        // submit, any flag present in the config but missing from the mirror was
        // silently dropped on every save — checkbox ticked, no error, value
        // gone. That is what happened to qr_loyalty. Deriving both from one
        // array removes the failure mode rather than patching this instance:
        // adding a flag to config/features.php is now sufficient.
        ...Object.fromEntries(
          gatedFeatures.map(f => [f.key, selectedFeature.includes(f.key)])
        ),

        upload_once_content_capacity: data.upload_once_content_capacity,
        media_storage_capacity: data.media_storage_capacity,
        playlist_capacity: data.playlist_capacity,
        email_quota: data.email_quota,
        multiple_layer: selectedLayers,
        orientation: data.orientation,
        media_type: selectedMediaTypes,
      },
      periods: selectedPeriods,
    };
    try {
      await adminApi.post(url, body);
      showToast('success', isEdit ? 'Updated' : 'Created', `Package ${isEdit ? 'updated' : 'created'} successfully.`);
      onDone?.();
    } catch (err) {
      const errs = err?.response?.data?.errors;
      if (errs) Object.values(errs).forEach(e => showToast('error', 'Validation Error', e[0]));
      else showToast('error', 'Error', err?.response?.data?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      {/* Stepper */}
      <div className="flex items-center gap-0">
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                i < step ? 'bg-teal text-white' :
                i === step ? 'bg-accent text-white' :
                'bg-white/[0.08] text-text-muted'
              }`}>
                {i < step ? '✓' : i + 1}
              </div>
              <span className={`text-[10px] hidden sm:block ${i === step ? 'text-accent font-medium' : 'text-text-muted'}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-2 mb-4 ${i < step ? 'bg-teal' : 'bg-white/[0.08]'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      <div className="min-h-[260px]">
        {step === 0 && <Step1 control={control} errors={errors} trigger={trigger} isCreation={!isEdit} showInDashboard={showInDashboard} setShowInDashboard={setShowInDashboard} />}
        {step === 1 && <Step2 errors={errors} clearErrors={clearErrors} setError={setError} />}
        {step === 2 && <Step3 errors={errors} clearErrors={clearErrors} setError={setError} />}
        {step === 3 && <Step4 control={control} errors={errors} />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-white/[0.08] pt-4">
        <button type="button" disabled={step === 0} onClick={() => setStep(s => s - 1)} className="btn-secondary disabled:opacity-40">
          ← Previous
        </button>
        {/* Distinct keys so React unmounts "Next" and mounts a fresh save button
            (not just flip type on the same node) — prevents the click that
            advanced to Step 4 from landing on a submit button. The save button
            is type="button" too (no native submit anywhere) and saves via an
            explicit handleSubmit onClick, so the Step 3→4 transition can never
            trigger a stray save. */}
        {step < STEPS.length - 1 ? (
          <button key="wiz-next" type="button" onClick={nextStep} className="btn-primary">Next →</button>
        ) : (
          <button key="wiz-save" type="button" disabled={saving} onClick={handleSubmit(onSubmit)} className="btn-primary">
            {saving ? <Spinner size="sm" /> : isEdit ? 'Update Package' : 'Create Package'}
          </button>
        )}
      </div>
    </form>
  );
}
